import classNames from 'classnames';
import { useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';
import { useIsLoggedIn } from '#src/hooks/auth/useIsLoggedIn.ts';
import { useScrollTo } from '#src/hooks/scrollTo/index.ts';
import { glossary } from '#src/lib/glossary.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

const Root = styled(Horizontal).attrs({ center: true, gap: 4 })`
  background-color: var(--c-nd-50-opaque);
  border-radius: var(--unit);
  padding: var(--unit) var(--triple-unit);
  @media ${laptopAndAbove} {
    display: flex;
    overflow: hidden;
  }
`;

const NavItemsContainer = styled(Horizontal).attrs({ center: true })`
  overflow-y: hidden;
  white-space: nowrap;
  border: 1px solid var(--c-nd-50);
  border-radius: var(--unit);
  padding: var(--half-unit);
  overflow-x: auto;
`;

const NavItem = styled(LabelL).attrs({ bold: true })`
  cursor: pointer;
  border-radius: var(--half-unit);
  padding: var(--unit) var(--double-unit);
  transition: background-color 0.2s ease;
  &:hover {
    background-color: var(--c-nd-50);
  }
  &.active {
    background-color: var(--c-nd-50);
    padding: var(--unit) var(--double-unit);
  }
`;

const navItems = defineMessages({
  home: {
    defaultMessage: 'Scout and collect',
  },
  about: {
    defaultMessage: 'Our competitions',
  },
  createATeam: {
    defaultMessage: 'Create a team',
  },
  score: {
    defaultMessage: 'Scoring',
  },
  rewards: {
    defaultMessage: 'Rewards',
  },
  gameplayStrategy: {
    defaultMessage: 'Level up',
  },
  cardScarcities: {
    defaultMessage: 'Your budget',
  },
});

export const Navbar = () => {
  const isLoggedIn = useIsLoggedIn();
  const { hash } = useLocation();
  const scrollTo = useScrollTo<string>((id: string) => id);
  const wrapperRef = useRef<HTMLDivElement>(null);

  return (
    <Root ref={wrapperRef}>
      <StarBall height={32} width={32} />
      <NavItemsContainer>
        {Object.entries(navItems).map(([key, message]) => {
          return (
            <ButtonBase key={key} onClick={() => scrollTo(key)}>
              <NavItem className={classNames({ active: hash === key })}>
                <FormattedMessage {...message} />
              </NavItem>
            </ButtonBase>
          );
        })}
      </NavItemsContainer>
      {!isLoggedIn && (
        <Button size="large">
          <FormattedMessage {...glossary.playNow} />
        </Button>
      )}
    </Root>
  );
};
